import React from 'react';

import { Text } from '@change/ds-core-ui';

import {
	useStarterDashboardV2PageData,
	useStarterDashboardV2TabsStateContext,
} from '#src/app/pages/starterDashboardV2/pageContext';
import { useRecommendedGroupPickerContext } from '#src/app/pages/starterDashboardV2/shared/components';
import type { PostPreviewSizes } from '#src/app/pages/starterDashboardV2/shared/components';
import {
	useMarkModuleAsLoaded,
	useReadyToShareContext,
	useRecommendations,
	useSharingModal,
} from '#src/app/pages/starterDashboardV2/shared/contexts';
import { useRetryPostGeneration } from '#src/app/pages/starterDashboardV2/shared/hooks';
import type { WaypointCallbackArgs } from '#src/app/shared/components/layout/lazy';
import { Waypoint } from '#src/app/shared/components/scroll';
import { useIsMobile } from '#src/app/shared/hooks/userAgent';

import { ScrollButtons } from '../shared/ScrollButtons';

import { PlatformFilter } from './components/PlatformFilter';
import { PostsCarousel } from './components/PostsCarousel';
import { useClickTracking, useGroupSelectionTracking, usePlatformFilter, useScroll, useViewTracking } from './hooks';

export const ReadyToSharePreDrafted: React.FC = () => {
	const isMobile = useIsMobile();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const {
		data: { selectedTab },
	} = useStarterDashboardV2TabsStateContext();
	const { data: contextData } = useReadyToShareContext();
	const { data: recommendationsData } = useRecommendations();

	const { startGroupSelection } = useRecommendedGroupPickerContext();
	const { startSharingFlow } = useSharingModal();

	const {
		data: { selectedPlatform, hasAnyValidContent, availablePlatforms, displayablePosts },
		actions: { selectPlatform, clearPlatformSelection },
	} = usePlatformFilter(contextData.postTypeData, recommendationsData, isMobile);

	// Retry functionality
	const {
		data: { retryStates },
		actions: { retryPostType },
	} = useRetryPostGeneration({ slug: petition.slug });

	const {
		data: { canScrollLeft, canScrollRight, scrollContainerRef },
		actions: { handleScroll },
	} = useScroll(selectedPlatform);
	const { trackView } = useViewTracking({ selectedTab });
	const { trackClick } = useClickTracking({ selectedTab });
	const { trackGroupSelection } = useGroupSelectionTracking({ selectedTab });

	const previewSize = (isMobile ? 'medium' : 'large') as PostPreviewSizes;

	useMarkModuleAsLoaded('ReadyToSharePreDrafted', contextData.shouldShowReadyToShareExperiment);

	if (!contextData.shouldShowReadyToShareExperiment) {
		return null;
	}

	// Hide module if no valid content is available and not loading
	if (!hasAnyValidContent) {
		return null;
	}

	return (
		<section
			id="ready-to-share-posts"
			data-qa="ready-to-share-posts"
			className="bg-background-base flex h-full w-full scroll-mt-20 flex-col px-4 py-8 md:scroll-mt-18 md:px-12 md:pt-8 md:pb-12 lg:py-12"
		>
			<Waypoint onPositionChange={(wp: WaypointCallbackArgs) => trackView(wp.currentPosition)} />

			<div className="mb-5">
				<Text as="h3" className="text-size-heading-small">
					Ready-to-share posts
				</Text>
			</div>
			<div className="flex justify-between align-middle">
				<PlatformFilter
					selectedPlatform={selectedPlatform}
					onClearSelection={clearPlatformSelection}
					onPlatformSelect={selectPlatform}
					availablePlatforms={availablePlatforms}
				/>
				{/* Scroll Buttons */}
				<ScrollButtons
					canScrollLeft={canScrollLeft}
					canScrollRight={canScrollRight}
					onScrollLeft={() => handleScroll('left')}
					onScrollRight={() => handleScroll('right')}
				/>
			</div>

			<PostsCarousel
				displayablePosts={displayablePosts}
				isMobile={isMobile}
				petition={petition}
				previewSize={previewSize}
				recommendationsData={recommendationsData}
				startSharingFlow={startSharingFlow}
				startGroupSelection={startGroupSelection}
				trackClick={trackClick}
				trackGroupSelection={trackGroupSelection}
				scrollContainerRef={scrollContainerRef}
				canScrollLeft={canScrollLeft}
				canScrollRight={canScrollRight}
				retryStates={retryStates}
				onRetry={retryPostType}
			/>
		</section>
	);
};
